// src/components/ReportLayoutSection.jsx
//
// Admin tab: per-block toggles controlling which sections the
// deterministic report assembler emits. Stored as a single
// `analysis.report.layout_json` setting; brand overrides are supported
// (currently the UI edits the global default, brand overrides will be
// surfaced from the brand admin page later).

import React, { useEffect, useMemo, useState, useCallback } from "react";

import { getReportLayout, putReportLayout } from "../services/api";

export default function ReportLayoutSection({ showToast }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schema, setSchema] = useState([]);
  const [layout, setLayout] = useState({});
  const [defaults, setDefaults] = useState({});
  const [source, setSource] = useState("default");
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getReportLayout();
      setSchema(data.schema || []);
      setLayout(data.layout || {});
      setDefaults(data.defaults || {});
      setSource(data.source || "default");
      setDirty(false);
    } catch (e) {
      showToast?.(`Failed to load report layout: ${e?.message || e}`, "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const acc = {};
    for (const block of schema) {
      const g = block.group || "Other";
      if (!acc[g]) acc[g] = [];
      acc[g].push(block);
    }
    return acc;
  }, [schema]);

  const toggle = (key) => {
    setLayout((cur) => ({ ...cur, [key]: !cur[key] }));
    setDirty(true);
  };

  const setGroupAll = (groupName, value) => {
    setLayout((cur) => {
      const next = { ...cur };
      for (const block of schema) {
        if ((block.group || "Other") === groupName) {
          next[block.key] = value;
        }
      }
      return next;
    });
    setDirty(true);
  };

  const resetToDefaults = () => {
    setLayout({ ...defaults });
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await putReportLayout({ scope_type: "global", layout });
      showToast?.("Report layout saved. New analysis runs will use this configuration.", "success");
      setDirty(false);
      // Reload to refresh "source" badge.
      await load();
    } catch (e) {
      showToast?.(`Save failed: ${e?.message || e}`, "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 24, color: "#6b7280" }}>Loading report layout…</div>;
  }

  const enabledCount = Object.values(layout).filter(Boolean).length;
  const totalCount = schema.length;
  const sourceBadge =
    source === "global"
      ? { label: "Custom (global)", bg: "#dbeafe", color: "#1d4ed8" }
      : source === "brand"
        ? { label: "Custom (brand)", bg: "#fef3c7", color: "#92400e" }
        : { label: "Defaults — all on", bg: "#dcfce7", color: "#166534" };

  return (
    <section>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Report Layout</h2>
          <p style={{ fontSize: 13, color: "#4b5563", margin: "6px 0 0", maxWidth: 720 }}>
            Toggle which blocks the deterministic assembler emits in the next analysis run.
            Disabling a block also skips the LLM call that would have generated its
            Insights & Actions, so removing sections lowers cost. Defaults to everything
            on; missing keys fall back to enabled to protect against stale stored configs.
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              padding: "4px 10px",
              borderRadius: 999,
              background: sourceBadge.bg,
              color: sourceBadge.color,
              whiteSpace: "nowrap",
            }}
          >
            {sourceBadge.label}
          </span>
          <span style={{ fontSize: 12, color: "#6b7280" }}>
            {enabledCount} / {totalCount} blocks on
          </span>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {Object.entries(grouped).map(([groupName, blocks]) => {
          const total = blocks.length;
          const on = blocks.filter((b) => layout[b.key]).length;
          return (
            <div
              key={groupName}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                background: "#ffffff",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 16px",
                  background: "#f9fafb",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
                  <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{groupName}</h3>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    {on} / {total} on
                  </span>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => setGroupAll(groupName, true)}
                    style={miniBtnStyle}
                  >
                    All on
                  </button>
                  <button
                    type="button"
                    onClick={() => setGroupAll(groupName, false)}
                    style={miniBtnStyle}
                  >
                    All off
                  </button>
                </div>
              </div>
              <div>
                {blocks.map((b) => {
                  const checked = !!layout[b.key];
                  return (
                    <label
                      key={b.key}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 12,
                        padding: "12px 16px",
                        borderBottom: "1px solid #f3f4f6",
                        cursor: "pointer",
                        opacity: checked ? 1 : 0.55,
                        transition: "opacity 0.15s",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(b.key)}
                        style={{ marginTop: 3, cursor: "pointer" }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{b.label}</div>
                        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                          {b.description}
                        </div>
                        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4, fontFamily: "ui-monospace, monospace" }}>
                          {b.key}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 20, display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={resetToDefaults}
          style={secondaryBtnStyle}
          disabled={saving}
        >
          Reset to defaults
        </button>
        <button
          type="button"
          onClick={() => load()}
          style={secondaryBtnStyle}
          disabled={saving || !dirty}
        >
          Discard changes
        </button>
        <button
          type="button"
          onClick={handleSave}
          style={{ ...primaryBtnStyle, opacity: dirty ? 1 : 0.5 }}
          disabled={!dirty || saving}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </section>
  );
}

const miniBtnStyle = {
  padding: "4px 10px",
  fontSize: 11,
  fontWeight: 600,
  border: "1px solid #d1d5db",
  borderRadius: 6,
  background: "#ffffff",
  color: "#1a1f36",
  cursor: "pointer",
};

const secondaryBtnStyle = {
  padding: "8px 14px",
  fontSize: 13,
  fontWeight: 600,
  border: "1px solid #d1d5db",
  borderRadius: 8,
  background: "#ffffff",
  color: "#1a1f36",
  cursor: "pointer",
};

const primaryBtnStyle = {
  padding: "8px 18px",
  fontSize: 13,
  fontWeight: 700,
  border: "1px solid #1a1f36",
  borderRadius: 8,
  background: "#1a1f36",
  color: "#ffffff",
  cursor: "pointer",
};
